import { layerStackFor, wrapIndex, sectionUrl, validateSmsSignup, buildSmsSubscriptionPayload } from './theme-lib.js';

const stage = document.querySelector('.stage');
const layers = {
  home: document.getElementById('layer-home'),
  product: document.getElementById('layer-product'),
  cart: document.getElementById('layer-cart')
};

// layout/password.liquid renders a bare <div class="stage stage--gate"> with no
// layer stack at all, so every layers.* lookup above is null on the gate. Nothing
// that dereferences them may run unless the whole stack is present — otherwise the
// module throws at top level and the gate's consent handling never binds.
const hasLayerStack = Boolean(stage && layers.home && layers.product && layers.cart);

// Fallback deadline for the close transition, read from the layer's OWN computed
// style rather than a constant mirrored from theme.css. Panels no longer share
// one duration -- the product panel slides slower -- and a deadline shorter than
// the real transition would hide a panel mid-slide. Reading it means the two can
// never drift apart. Under prefers-reduced-motion this resolves to 0s, so the
// deadline collapses to the 80ms grace and the panel hides promptly.
function panelDeadline(layer) {
  const raw = getComputedStyle(layer).transitionDuration || '0s';
  const ms = raw.split(',').reduce((max, part) => {
    const value = parseFloat(part);
    if (Number.isNaN(value)) return max;
    return Math.max(max, part.trim().endsWith('ms') ? value : value * 1000);
  }, 0);
  return ms + 80;
}

// Section Rendering only resolves a section_id for STATICALLY rendered
// sections. Sections declared in a JSON template get a dynamic id
// (template--1234__main) we cannot know from another page, so those panels are
// lifted out of a normal page fetch instead. cart-panel is rendered statically
// by templates/cart.liquid, so it keeps the cheaper section fetch — fetched from
// /cart, the only URL that actually renders it, and the correct cart context.
async function loadFragment(url, selector, sectionId) {
  const target = sectionId ? sectionUrl(url, sectionId) : url;
  const response = await fetch(target, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
  if (!response.ok) throw new Error(`Fetch failed: ${response.status}`);
  const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
  const node = selector ? doc.querySelector(selector) : doc.body;
  if (!node) throw new Error(`No ${selector} in response`);
  return node.innerHTML;
}

// [hidden] is display:none !important, so a layer un-hidden and opened in the
// same synchronous block has no starting style and the CSS transition is skipped
// entirely. Un-hide, force a layout flush, then open on the next style resolve.
const pendingHide = new WeakMap();

function cancelPendingHide(layer) {
  const pending = pendingHide.get(layer);
  if (!pending) return;
  layer.removeEventListener('transitionend', pending.onEnd);
  clearTimeout(pending.timer);
  pendingHide.delete(layer);
}

// Closing is the mirror problem: set hidden immediately and the slide-out never
// draws. Defer it to transitionend, with a timeout deadline so a dropped event —
// or prefers-reduced-motion, where transition:none means transitionend never
// fires at all — can never strand a panel visible.
function hideAfterTransition(layer) {
  cancelPendingHide(layer);
  const finish = () => {
    cancelPendingHide(layer);
    if (!layer.classList.contains('is-open')) layer.hidden = true;
  };
  const onEnd = (event) => {
    if (event.target === layer && event.propertyName === 'transform') finish();
  };
  layer.addEventListener('transitionend', onEnd);
  pendingHide.set(layer, { onEnd, timer: setTimeout(finish, panelDeadline(layer)) });
}

function setLayerOpen(layer, open) {
  if (open) {
    cancelPendingHide(layer);
    if (layer.hidden) {
      layer.hidden = false;
      void layer.offsetWidth; // flush layout so translateX(100%) is the start value
    }
    layer.classList.add('is-open');
    return;
  }
  if (layer.hidden || !layer.classList.contains('is-open')) {
    layer.classList.remove('is-open');
    return;
  }
  layer.classList.remove('is-open');
  hideAfterTransition(layer);
}

// The WINDOW scrolls exactly one layer -- .is-flow, the topmost open one (see
// .layer.is-flow in theme.css for why the document scrolls at all). Handing
// the flow to another layer freezes the current one where it stands: its
// window scroll position is copied into its own scrollTop (it is fixed with
// overflow: hidden once it leaves the flow, so that shifts its content by
// exactly what the window had scrolled) and remembered. The new flow layer
// gets the window scrolled back to whatever it had when it was covered, or the
// top. All of it happens in one task, so the swap never paints half-done.
const savedScroll = new WeakMap();
let flowing = document.querySelector('.layer.is-flow');

function setFlow(layer) {
  if (!layer || flowing === layer) return;
  if (flowing) {
    const y = window.scrollY;
    savedScroll.set(flowing, y);
    flowing.classList.remove('is-flow');
    // Desktop freezes a FIXED layer by scrolling it (scrollTop); phones freeze
    // an ABSOLUTE one by lifting it (--frozen-y) -- see the phone block after
    // .layer--product in theme.css for why phones cannot use fixed. Both are
    // set; each viewport's CSS reads only its own.
    flowing.scrollTop = y;
    flowing.style.setProperty('--frozen-y', `${y}px`);
  }
  layer.classList.add('is-flow');
  layer.style.removeProperty('--frozen-y');
  layer.scrollTop = 0;
  flowing = layer;
  window.scrollTo(0, savedScroll.get(layer) || 0);
  onWindowScroll();
}

// Covered layers dim and stop taking taps; the topmost open one takes the flow.
function applyStack(stack) {
  setLayerOpen(layers.product, stack.product);
  setLayerOpen(layers.cart, stack.cart);
  layers.home.classList.toggle('is-dimmed', stack.product || stack.cart);
  layers.product.classList.toggle('is-dimmed', stack.cart);
  stage.classList.toggle('has-overlay', stack.product || stack.cart);
  setFlow(stack.cart ? layers.cart : stack.product ? layers.product : layers.home);
}

// How many history entries THIS controller pushed. Close may only run
// history.back() when it is unwinding one of our own entries; on a direct entry
// to /products/<handle> (the Instagram ad path) the previous entry belongs to
// Instagram, and back() would eject the shopper off-site.
let pushedEntries = 0;

function pushPanelState(url) {
  history.pushState({ path: url }, '', url);
  pushedEntries += 1;
}

// Shared in-flight guard: two taps during a section fetch (no visual feedback
// while it loads) otherwise both reach pushPanelState, pushedEntries goes to
// 2 on one shopper action, and the next [Close] tap's history.back() unwinds
// only one of them, so the panel appears to not close. The finally below
// always clears the flag, even if the fetch throws, so it can never latch on.
let opening = false;

// The footer lives in the homepage's JSON template only, so the product panel
// borrows a copy of it rather than rendering a second footer section whose
// links would have to be kept in step by hand in the theme editor. Appended to
// the LAYER, after the panel, so it runs full-bleed past the panel's padding
// and the layer's dim (> *) covers it too. The white wordmark comes along: its
// crop falls back to the base .home-grid__mark-crop rule (absolute, top 0,
// full width, 100svh) since the fixed one is scoped to .layer--home, and the
// footer is itself 100svh, so the mark lands where the homepage puts it once
// the footer fills the screen -- it scrolls in with the footer rather than
// changing colour, there being no green mark behind it here.
// Shown on phones only -- theme.css hides .footer--panel above 520px.
// No JavaScript, no copy: the product page simply ends without a footer.
function attachFooter(layer) {
  if (!layer || layer.querySelector('[data-footer]')) return;
  const source = layers.home.querySelector('[data-footer]');
  if (!source) return;
  const copy = source.cloneNode(true);
  copy.classList.add('footer--panel');
  layer.appendChild(copy);
}

async function openProduct(url, { push = true } = {}) {
  if (opening) return;
  opening = true;
  try {
    try {
      layers.product.innerHTML = await loadFragment(url, '#layer-product', null);
    } catch (error) {
      window.location.href = url; // never leave a dead panel
      return;
    }
    attachFooter(layers.product);
    savedScroll.delete(layers.product); // new product: start at its top
    if (push) pushPanelState(url);
    applyStack(layerStackFor(new URL(url, location.origin).pathname));
    bindPanel(layers.product);
  } finally {
    opening = false;
  }
}

async function openCart({ push = true, html = null } = {}) {
  if (opening) return;
  opening = true;
  const url = '/cart';
  try {
    try {
      // Add to Cart hands over the panel it already got back with the add
      // (see onAddToBag), saving a second round trip before the slide.
      layers.cart.innerHTML = html || await loadFragment(url, null, 'cart-panel');
    } catch (error) {
      window.location.href = url;
      return;
    }
    savedScroll.delete(layers.cart);
    // A swipe-close leaves the layer parked at an inline translateX(100%).
    layers.cart.style.transform = '';
    if (push) pushPanelState(url);
    applyStack({ product: layers.product.classList.contains('is-open'), cart: true });
    bindPanel(layers.cart);
  } finally {
    opening = false;
  }
}

// Close is history.back() ONLY when we have an entry of our own to unwind, so
// the close control and the browser back button stay the same operation. With
// nothing pushed — direct entry, or a landing after a native cart form post —
// the control falls through to its own href and navigates normally.
function onCloseClick(event) {
  const trigger = event.target.closest('[data-close-layer]');
  if (!trigger) return;
  if (pushedEntries <= 0) return;
  event.preventDefault();
  history.back();
}

// Closing the cart any way other than its [Close] link goes THROUGH that link,
// so it takes the same path: history.back() when we pushed the entry, the
// link's own href otherwise (direct entry to /cart).
function closeCart() {
  const link = layers.cart.querySelector('[data-close-layer]');
  if (link) link.click();
}

// A tap on the dimmed page beside the cart closes it. The covered layers take
// no taps (pointer-events: none), so such a tap lands on the stage behind them.
function onOutsideCartClick(event) {
  if (!layers.cart.classList.contains('is-open')) return;
  if (event.target !== stage && event.target !== document.body) return;
  closeCart();
}

// Swipe the cart to the right to close it, touch only. The layer follows the
// finger; past a quarter of its width or a quick flick it carries on out and
// the cart closes, otherwise it springs back. touch-action: pan-y (theme.css)
// keeps vertical drags scrolling the bag -- the browser cancels the pointer on
// those -- and a drag only counts once it is clearly sideways, so taps on
// Remove, Add and Checkout are untouched.
function bindCartSwipe() {
  const cart = layers.cart;
  let startX = 0;
  let startY = 0;
  let startT = 0;
  let dx = 0;
  let tracking = false;
  let dragging = false;
  const reset = () => {
    tracking = false;
    dragging = false;
    dx = 0;
  };
  cart.addEventListener('pointerdown', (event) => {
    if (event.pointerType === 'mouse' || !cart.classList.contains('is-open')) return;
    tracking = true;
    dragging = false;
    startX = event.clientX;
    startY = event.clientY;
    startT = performance.now();
    dx = 0;
  });
  cart.addEventListener('pointermove', (event) => {
    if (!tracking) return;
    const moveX = event.clientX - startX;
    const moveY = event.clientY - startY;
    if (!dragging) {
      if (Math.abs(moveX) < 10 || Math.abs(moveX) < Math.abs(moveY)) return;
      dragging = true;
      cart.style.transition = 'none';
    }
    dx = Math.max(0, moveX);
    cart.style.transform = `translateX(${dx}px)`;
  });
  const end = () => {
    if (!tracking) return;
    const wasDragging = dragging;
    const width = cart.offsetWidth || 1;
    const flick = dx > 40 && performance.now() - startT < 250;
    const close = wasDragging && (dx > width * 0.25 || flick);
    reset();
    if (!wasDragging) return;
    // Hand back to the stylesheet's transition so the rest of the move animates.
    cart.style.transition = '';
    if (close) {
      cart.style.transform = 'translateX(100%)';
      closeCart();
    } else {
      cart.style.transform = '';
    }
  };
  cart.addEventListener('pointerup', end);
  cart.addEventListener('pointercancel', end);
}

function onCardClick(event) {
  const link = event.target.closest('a[data-product-url]');
  if (!link || event.metaKey || event.ctrlKey || event.shiftKey) return;
  event.preventDefault();
  if (opening) return;
  openProduct(link.dataset.productUrl);
}

// Shop grid Product / Model switch. The view lives on <html>, not on the grid,
// because the home layer's markup is replaced wholesale on a direct product
// entry (hydrateDirectEntry) and a class on the grid would be lost with it.
// theme.liquid restores the saved value before first paint.
const GRID_VIEW_KEY = 'verdi-grid-view';

function syncViewButtons() {
  const view = document.documentElement.dataset.gridView || 'product';
  document.querySelectorAll('[data-view-set]').forEach((btn) => {
    btn.setAttribute('aria-pressed', String(btn.dataset.viewSet === view));
  });
}

function onViewToggle(event) {
  const btn = event.target.closest('[data-view-set]');
  if (!btn) return;
  const view = btn.dataset.viewSet === 'model' ? 'model' : 'product';
  if (view === 'model') document.documentElement.dataset.gridView = 'model';
  else delete document.documentElement.dataset.gridView;
  try { localStorage.setItem(GRID_VIEW_KEY, view); } catch (error) { /* private mode */ }
  syncViewButtons();
}

function onCartClick(event) {
  const trigger = event.target.closest('[data-open-cart]');
  if (!trigger || event.metaKey || event.ctrlKey || event.shiftKey) return;
  event.preventDefault();
  if (opening) return;
  openCart();
}

function bindSlideshow(root) {
  const show = root.querySelector('[data-slideshow]');
  if (!show) return;
  const track = show.querySelector('[data-slideshow-track]');
  const slides = [...show.querySelectorAll('[data-slide]')];
  if (!track || slides.length < 2) return;
  let index = 0;
  // Move the track, do not show/hide slides: CSS cannot animate between two
  // elements swapping display. aria-hidden keeps the off-screen slides out of
  // the accessibility tree, which `hidden` used to do implicitly.
  const segs = [...show.querySelectorAll('[data-seg]')];
  const count = show.querySelector('[data-count]');
  const render = () => {
    track.style.transform = `translateX(-${index * 100}%)`;
    slides.forEach((slide, i) => slide.setAttribute('aria-hidden', String(i !== index)));
    segs.forEach((seg, i) => seg.classList.toggle('is-active', i === index));
    if (count) count.textContent = String(index + 1);
  };
  show.addEventListener('click', (event) => {
    const arrow = event.target.closest('[data-arrow]');
    if (!arrow) return;
    index = wrapIndex(index + (arrow.dataset.arrow === 'next' ? 1 : -1), slides.length);
    render();
  });

  // Swipe, touch only -- a mouse keeps the arrows. Opted into per slideshow
  // with [data-swipe]: the landing hero is deliberately one still photograph on
  // a phone and must not move. The stage carries touch-action: pan-y, so a
  // vertical drag still scrolls the page and the browser cancels the pointer;
  // only horizontal drags reach here. The track follows the finger, then
  // settles on the next slide past a 15% throw or a quick flick. Swipes stop at
  // the ends rather than wrapping -- dragging past the last photo should not
  // fling you back to the first.
  const stage = show.querySelector('.slideshow__stage');
  if (show.hasAttribute('data-swipe') && stage) {
    let startX = 0;
    let startT = 0;
    let dx = 0;
    let dragging = false;
    const end = () => {
      if (!dragging) return;
      dragging = false;
      track.style.transition = '';
      const width = stage.clientWidth || 1;
      const flick = Math.abs(dx) > 30 && performance.now() - startT < 250;
      if (Math.abs(dx) > width * 0.15 || flick) {
        index = Math.min(slides.length - 1, Math.max(0, index + (dx < 0 ? 1 : -1)));
      }
      dx = 0;
      render();
    };
    stage.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'mouse') return;
      dragging = true;
      startX = event.clientX;
      startT = performance.now();
      dx = 0;
      track.style.transition = 'none';
    });
    stage.addEventListener('pointermove', (event) => {
      if (!dragging) return;
      dx = event.clientX - startX;
      // Resist past either end, so the edge is felt rather than shown as a gap.
      if ((index === 0 && dx > 0) || (index === slides.length - 1 && dx < 0)) dx /= 3;
      track.style.transform = `translateX(calc(-${index * 100}% + ${dx}px))`;
    });
    stage.addEventListener('pointerup', end);
    stage.addEventListener('pointercancel', end);
  }
  render();
}

function showAddError(node, message) {
  if (!node) return;
  node.textContent = message;
  node.hidden = false;
}

// The add runs from the button's CLICK, which is cancelled, so the form never
// fires a submit event. Shopify's own tracking listens for product-form
// submits: it sent Meta an AddToCart for the page's default size on the submit,
// then a second, correct one when /cart/add.js answered -- two event ids, so
// Meta counted every add twice (verified live 2026-10-28; a bare /cart/add.js
// fires exactly one). onAddToBag stays on submit for an Enter-key submit.
// Document-level because the landing sticky button sits outside the form and
// outside any layer, pointing in through the form attribute -- button.form
// resolves it either way.
function onAddClick(event) {
  const button = event.target.closest('[data-atb]');
  const form = button && button.form;
  if (!form || !form.matches('[data-atb-form]')) return;
  event.preventDefault();
  addToBag(form);
}

async function onAddToBag(event) {
  const form = event.target.closest('[data-atb-form]');
  if (!form) return;
  event.preventDefault();
  await addToBag(form);
}

async function addToBag(form) {
  const error = form.querySelector('[data-atb-error]');
  if (error) error.hidden = true;

  // A double tap otherwise posts twice and lands quantity 2. ALL of them, not
  // just the first: the landing page has a second [data-atb] in its sticky bar,
  // and querySelector would have locked the main button while leaving the
  // sticky one live — the same double-add this lock exists to prevent, just
  // moved to the other control. form.elements, not querySelectorAll: the sticky
  // button sits OUTSIDE the form and joins it through the form attribute, which
  // only form.elements follows. Only lock buttons that were not already
  // disabled, so a sold-out button is never re-enabled.
  const locked = Array.from(form.elements).filter((b) => b.matches('[data-atb]') && !b.disabled);
  locked.forEach((b) => { b.disabled = true; });

  try {
    // sections=cart-panel: Shopify renders the refreshed cart panel into the
    // add response itself, so the panel slides out after ONE round trip
    // instead of an add followed by a separate fetch of /cart.
    const body = new FormData(form);
    body.append('sections', 'cart-panel');
    body.append('sections_url', '/cart');
    const response = await fetch('/cart/add.js', { method: 'POST', body });
    if (!response.ok) {
      // Sold out or unavailable: show Shopify's message, do NOT open the cart.
      const failure = await response.json().catch(() => ({}));
      showAddError(error, failure.description || failure.message || 'Could not add to bag');
      return;
    }
    const added = await response.json().catch(() => ({}));
    const html = added.sections && added.sections['cart-panel'];
    // Awaited so the button re-enable in finally happens only once the cart
    // panel is actually up and the product panel dimmed — otherwise the lock
    // lifts mid-fetch and a second tap posts a second unit, the same
    // double-add this lock exists to prevent, just moved later.
    await openCart({ html });
  } catch (requestError) {
    // Offline, DNS failure, timeout, content blocker: the request never landed.
    showAddError(error, 'Could not add to bag. Check your connection and try again.');
  } finally {
    locked.forEach((b) => { b.disabled = false; });
  }
}

// Pinned logo bar on the phone product panel: the white bar and the logo
// fade in once the page has scrolled a little. 30px, not 0: iPhone Safari can
// rest a few pixels off the top, and > 0 showed the bar over the top of the
// photo before any scroll at all. Reads the WINDOW, which
// scrolls whichever layer is .is-flow; a frozen layer's head keeps whatever
// state it had when it was covered. A function declaration, so setFlow can
// call it before this point in the file runs.
function onWindowScroll() {
  // The rubber band past the end of the page shows the html background, and
  // Safari has only the one colour for both ends. So it follows the scroll:
  // footer green while the flow layer's footer fills the lower half of the
  // screen, the ordinary ground otherwise -- green below the footer, never
  // above the top of a white page.
  const footer = flowing && flowing.querySelector('[data-footer]');
  const atFooter = Boolean(footer && footer.offsetParent !== null &&
    footer.getBoundingClientRect().top < window.innerHeight / 2);
  document.documentElement.classList.toggle('is-at-footer', atFooter);

  const head = flowing && flowing.querySelector('[data-panel-head]');
  if (!head) return;
  head.classList.toggle('is-stuck', window.scrollY > 30);
}
window.addEventListener('scroll', onWindowScroll, { passive: true });

// Remove, in place. The form still works as a plain post with JavaScript off
// (or if anything here fails -- see the catch); with it on, the last unit of a
// line fades out slowly before the cart re-renders, instead of the whole page
// reloading. A line that is only going DOWN a unit (3 -> 2) has nothing to
// fade and simply re-renders with its new quantity. The change request runs
// alongside the fade so the wait is the fade, not fade plus network.
async function onCartRemove(event) {
  const form = event.target.closest('[data-cart-remove]');
  if (!form) return;
  event.preventDefault();
  const button = form.querySelector('button');
  if (button) button.disabled = true;
  const line = form.closest('.cart__line');
  const removing = Number(form.elements.quantity.value) === 0;
  try {
    const change = fetch('/cart/change.js', {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: new FormData(form)
    }).then((response) => {
      if (!response.ok) throw new Error(`Change failed: ${response.status}`);
    });
    if (removing && line) {
      line.classList.add('is-removing');
      await new Promise((resolve) => setTimeout(resolve, panelDeadline(line)));
    }
    await change;
    layers.cart.innerHTML = await loadFragment('/cart', null, 'cart-panel');
    bindPanel(layers.cart);
  } catch (error) {
    // Never leave a half-faded line: fall back to the ordinary form post.
    // form.submit() does not fire a submit event, so this cannot loop.
    form.submit();
  }
}

function bindPanel(root) {
  bindSlideshow(root);
  root.addEventListener('submit', onAddToBag);
  root.addEventListener('submit', onCartRemove);
  onWindowScroll();
}

// Direct entry: the layout has already server-rendered the panel into its own
// layer (see the content_for_layout routing in theme.liquid), so the only
// missing piece is the homepage behind it for the sliver.
async function hydrateDirectEntry() {
  // The landing page lives at a PRODUCT url, so layerStackFor reads it as a
  // product panel and this would bind the wrong layer and then overwrite the
  // landing page itself with the homepage fragment. The path cannot distinguish
  // them; the layout marks the page instead.
  if (document.body.dataset.landing) return;
  const stack = layerStackFor(location.pathname);
  const target = stack.cart ? layers.cart : stack.product ? layers.product : null;
  if (!target) return;

  bindPanel(target);
  try {
    layers.home.innerHTML = await loadFragment('/', '#layer-home', null);
    syncViewButtons();
    if (target === layers.product) attachFooter(target);
  } catch (error) {
    // Sliver falls back to flat white; not worth a full navigation.
  }
  applyStack(stack);
}

// --- Gate -----------------------------------------------------------------
// Bound BEFORE the layer-stack bootstrap and independently of it: the gate is
// the one page that has no layer stack, and consent is a hard stop that must
// never be skipped because some unrelated overlay code failed.
// xxx-xxx-xxxx while typing. Grouping a number as it is entered is most of the
// difference between a field that feels checked and one that feels like a plain
// text box, and it makes a transposed digit visible at a glance.
//
// Digits only, capped at ten. An entry starting with "+" is left ALONE: toE164
// accepts international numbers of 8 to 15 digits and NANP grouping would
// mangle every one of them. Purely cosmetic either way -- toE164 strips all
// separators before the number is validated or sent, so this cannot disagree
// with what Klaviyo receives, and with JavaScript off it simply never runs.
function groupNanpDigits(value) {
  const digits = value.replace(/\D/g, '').slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
}

// ONE SUBMIT PATH for every SMS signup in the theme. The password gate and the
// discount popup both call this: they ask for different fields and report
// success differently, but the validation, the payload and the Klaviyo call are
// identical, and a fix to any of them has to land in both. Two copies of this
// would be two copies of a double-opt-in contract.
//
// Fields are read DEFENSIVELY rather than assumed present -- the gate collects a
// surname and an optional email, the popup collects neither, and
// buildSmsSubscriptionPayload treats both as optional. A missing input yields an
// empty string rather than a TypeError on `.value`.
async function submitSmsForm(form, { errors, done, onSuccess, source }) {
  const field = (n) => form.elements[n] || null;
  const value = (n) => (field(n) ? field(n).value : '');
  const firstName = value('first_name');
  const lastName = value('last_name');

  const fields = {
    firstName,
    lastName,
    email: value('email'),
    phone: value('phone'),
    consent: field('consent') ? field('consent').checked : false,
    // The pre-split shape as well, for a browser running a CACHED theme-lib.js
    // that has never heard of firstName/lastName. Observed live on 2026-09-05:
    // this file is cache-busted by asset_url's ?v= and its './theme-lib.js'
    // specifier is not, so the two can be different vintages. A stale lib reads
    // `name` and still subscribes the visitor -- it just misses the surname and
    // email until the cache turns over. A changed signature alone would have
    // failed every signup with "Enter your first name" instead.
    name: [firstName, lastName].map((part) => (part || '').trim()).filter(Boolean).join(' ')
  };

  const problems = validateSmsSignup(fields);
  if (problems.length) {
    errors.textContent = problems.join('. ');
    errors.hidden = false;
    return;
  }
  errors.hidden = true;

  const key = document.body.dataset.klaviyoKey;
  const listId = document.body.dataset.klaviyoList;
  if (!key || !listId) {
    errors.textContent = 'SMS signup is not configured yet.';
    errors.hidden = false;
    return;
  }

  const payload = buildSmsSubscriptionPayload({
    ...fields,
    listId,
    // Names the surface in Klaviyo's custom_source -- see the note in
    // buildSmsSubscriptionPayload for why the discount flow depends on it.
    source,
    // Seconds precision, not Date#toISOString's milliseconds, which Klaviyo
    // rejects. Trimmed HERE as well as in buildSmsSubscriptionPayload: this
    // file is cache-busted by asset_url's ?v= but its './theme-lib.js'
    // specifier is not, so a browser can pair a fresh theme.js with a stale
    // lib. Normalising at the call site keeps the payload valid either way.
    consentedAt: new Date().toISOString().replace(/\.\d{3}Z$/, 'Z')
  });

  try {
    const response = await fetch(
      `https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(key)}`,
      {
        method: 'POST',
        headers: { 'content-type': 'application/json', revision: '2025-07-15' },
        body: JSON.stringify(payload)
      }
    );

    if (response.ok) {
      form.reset();
      onSuccess();
    } else {
      errors.textContent = 'Could not sign you up. Try again.';
      errors.hidden = false;
    }
  } catch (error) {
    errors.textContent = 'Could not sign you up. Try again.';
    errors.hidden = false;
  }
}

// Product-page footer email signup. Delegated from the document because the
// form lives in a CLONE of the homepage footer that attachFooter creates fresh
// on every panel open. Email-only subscription, built here rather than in
// theme-lib.js -- see the warning at the popstate handler about never adding a
// new lib export for theme.js to import.
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

async function onFooterSignup(event) {
  const form = event.target.closest('[data-footer-signup]');
  if (!form) return;
  event.preventDefault();
  const msg = form.querySelector('[data-signup-msg]');
  const button = form.querySelector('button');
  const input = form.elements.email;
  const say = (text) => { msg.textContent = text; msg.hidden = false; };
  const email = (form.elements.email.value || '').trim();
  if (!EMAIL_PATTERN.test(email)) { say('Enter a valid email address.'); return; }

  const key = document.body.dataset.klaviyoKey;
  const listId = form.dataset.list || document.body.dataset.klaviyoList;
  if (!key || !listId) { say('Email signup is not configured yet.'); return; }

  button.disabled = true;
  try {
    const response = await fetch(
      `https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(key)}`,
      {
        method: 'POST',
        headers: { 'content-type': 'application/json', revision: '2025-07-15' },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              custom_source: 'Product page footer',
              profile: {
                data: {
                  type: 'profile',
                  attributes: {
                    email,
                    subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } }
                  }
                }
              }
            },
            relationships: { list: { data: { type: 'list', id: listId } } }
          }
        })
      }
    );
    if (!response.ok) throw new Error(`Klaviyo ${response.status}`);
    form.reset();
    // Drop the keyboard: there is no button to look at, so the message below
    // the line is the only confirmation and it should not be hidden behind it.
    input.blur();
    say("You're on the list.");
  } catch (error) {
    say('Could not sign you up. Try again.');
  } finally {
    button.disabled = false;
  }
}
document.addEventListener('submit', onFooterSignup);

// xxx-xxx-xxxx as you type, for whichever form is passed. Extracted alongside
// the submit path for the same reason: the popup's number field has to behave
// like the gate's, and a second copy is a second thing to fix.
function bindPhoneGrouping(form) {
  const phoneField = form.elements.phone;
  if (!phoneField) return;

  phoneField.addEventListener('input', () => {
    if (phoneField.value.trim().startsWith('+')) return;

    const grouped = groupNanpDigits(phoneField.value);
    if (grouped === phoneField.value) return;

    // The caret is restored by how many DIGITS preceded it, not by string
    // index. Reassigning .value drops the caret to the end, and counting
    // characters instead would shift it a place left every time a dash is
    // inserted -- so editing the middle of a number would walk backwards.
    const digitsBefore = phoneField.value
      .slice(0, phoneField.selectionStart || 0)
      .replace(/\D/g, '').length;

    phoneField.value = grouped;

    let index = 0;
    let seen = 0;
    while (index < grouped.length && seen < digitsBefore) {
      if (/\d/.test(grouped[index])) seen += 1;
      index += 1;
    }
    phoneField.setSelectionRange(index, index);
  });
}

// --- Password gate signup -------------------------------------------------
const gateForm = document.querySelector('[data-gate-form]');

if (gateForm) {
  const errors = gateForm.querySelector('[data-gate-errors]');
  const done = gateForm.querySelector('[data-gate-done]');

  // Klaviyo SMS is DOUBLE opt-in: a 2xx means the confirmation text was sent,
  // not that anyone joined. The visitor is only on the list once they reply YES
  // to it, so that instruction has to be the thing they see.
  //
  // The inline <p> is the fallback, not the default. showModal is feature
  // detected rather than assumed, and the call is guarded: showModal throws
  // InvalidStateError if the dialog is already open, which it can be if a
  // second submit lands before the first is dismissed.
  const confirmModal = document.querySelector('[data-gate-confirm]');
  const onSuccess = () => {
    if (confirmModal && typeof confirmModal.showModal === 'function' && !confirmModal.open) {
      confirmModal.showModal();
    } else if (!confirmModal || typeof confirmModal.showModal !== 'function') {
      done.hidden = false;
    }
  };
  const submit = () => submitSmsForm(gateForm, { errors, done, onSuccess, source: 'Verdi mobile gate' });

  // The control is type="button": the form carries no action and no method, so a
  // native submit would GET the current URL with the shopper's name and phone
  // number in the query string -- into history, server logs and the Referer
  // header. With JS off it must be inert, not leaky. The submit listener only
  // covers implicit submission and is a no-op belt on top of that.
  const trigger = gateForm.querySelector('[data-gate-submit]');
  if (trigger) trigger.addEventListener('click', submit);

  // Esc and the backdrop are handled by <dialog> itself; this is only the button.
  // Bound here rather than at open time so it is attached once, not once per
  // signup -- showModal can run more than once in a session.
  if (confirmModal) {
    const closer = confirmModal.querySelector('[data-gate-confirm-close]');
    if (closer) closer.addEventListener('click', () => confirmModal.close());
  }

  bindPhoneGrouping(gateForm);
  gateForm.addEventListener('submit', (event) => {
    event.preventDefault();
    submit();
  });
}

// --- SMS discount popup ---------------------------------------------------
// Home page and ad landing page only; the layout decides which. Everything here
// is guarded on the dialog existing, so the whole block is inert when the
// setting is off (the snippet renders nothing at all).
const smsPopup = document.querySelector('[data-sms-popup]');

if (smsPopup) {
  const POPUP_STORAGE_KEY = 'verdi:sms-popup';

  // Shopify sets Shopify.designMode ONLY inside the theme editor's preview
  // iframe, never on the live storefront. Two things hang off it, both so the
  // popup can actually be worked on:
  //
  //   1. it opens every reload, ignoring dismissed/subscribed state. Otherwise
  //      the first time you close it while editing the copy, it is gone for the
  //      configured 30 days and the only way back is clearing site data.
  //   2. it writes NOTHING. Testing a signup in the editor would otherwise
  //      leave 'subscribed' in localStorage for that browser, which would then
  //      suppress the popup on the real storefront for the person who was only
  //      previewing it.
  //
  // Guarded through window.Shopify because this file also runs on the local
  // harness, where the global does not exist at all.
  const inThemeEditor = Boolean(window.Shopify && window.Shopify.designMode);

  // localStorage THROWS, it does not merely return null: Safari's private mode
  // and a browser set to block site data both raise on access. A popup is a
  // convenience, so every read and write is wrapped and failure degrades to
  // "no memory" -- the popup shows again next visit rather than the script
  // dying and taking the rest of the page's behaviour with it.
  const readState = () => {
    try {
      return JSON.parse(localStorage.getItem(POPUP_STORAGE_KEY) || 'null');
    } catch (error) {
      return null;
    }
  };
  const writeState = (state) => {
    if (inThemeEditor) return;
    try {
      localStorage.setItem(POPUP_STORAGE_KEY, JSON.stringify({ state, at: Date.now() }));
    } catch (error) {
      /* no memory available; the popup simply returns next visit */
    }
  };

  const form = smsPopup.querySelector('[data-sms-form]');
  const errors = smsPopup.querySelector('[data-sms-errors]');
  const done = smsPopup.querySelector('[data-sms-done]');

  // Subscribed is FOREVER, dismissed expires. Someone who gave you their number
  // should never be asked again on this device; someone who closed it is asked
  // again after the configured interval.
  const shouldShow = () => {
    if (inThemeEditor) return true;
    const saved = readState();
    if (!saved) return true;
    if (saved.state === 'subscribed') return false;
    const days = Number(smsPopup.dataset.reshowDays) || 30;
    return Date.now() - (saved.at || 0) > days * 24 * 60 * 60 * 1000;
  };

  const close = (state) => {
    writeState(state);
    if (smsPopup.open) smsPopup.close();
  };

  const dismiss = smsPopup.querySelector('[data-sms-dismiss]');
  if (dismiss) dismiss.addEventListener('click', () => close('dismissed'));

  // Esc and the backdrop close the dialog without going through the button, and
  // both mean the same thing as clicking it. Without this they would close the
  // popup and remember nothing, so it would reopen on the next page view.
  // Guarded on the success state so a subscriber is not downgraded to dismissed
  // by the close that follows their own signup.
  smsPopup.addEventListener('close', () => {
    const saved = readState();
    if (!saved || saved.state !== 'subscribed') writeState('dismissed');
  });

  if (form) {
    const onSuccess = () => {
      // The fields go, the instruction stays: the visitor still has to reply YES
      // before the code is sent, so the dialog holds that sentence rather than
      // closing on a success they have not finished yet.
      form.querySelectorAll('input, .gate__consent, [data-sms-submit]').forEach((el) => {
        el.hidden = true;
      });
      done.hidden = false;
      writeState('subscribed');
    };
    // The string the Klaviyo flow filters on to decide who gets the code.
    // Change it here and change it in the flow, or the discount stops sending.
    const submitPopup = () => submitSmsForm(form, { errors, done, onSuccess, source: 'Verdi discount popup' });

    const trigger = form.querySelector('[data-sms-submit]');
    if (trigger) trigger.addEventListener('click', submitPopup);

    bindPhoneGrouping(form);
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      submitPopup();
    });
  }

  // showModal is feature detected, not assumed. Without <dialog> support the
  // element never opens and stays display:none -- the page is simply the page,
  // which is the same thing that happens with JavaScript disabled.
  if (typeof smsPopup.showModal === 'function' && shouldShow()) {
    const delay = Number(smsPopup.dataset.delay);
    window.setTimeout(() => {
      // Re-checked at fire time, not only when scheduled: the visitor may have
      // signed up through another surface, or opened the page in a second tab,
      // during the delay.
      if (!smsPopup.open && shouldShow()) smsPopup.showModal();
    }, (Number.isFinite(delay) ? delay : 8) * 1000);
  }
}

// --- Size chart -----------------------------------------------------------
// Bound at the document, and OUTSIDE the hasLayerStack guard below. Delegation
// is required rather than tidy: openProduct replaces the whole product layer
// with `layers.product.innerHTML = ...`, so a listener bound to a button inside
// that panel is discarded on the next card tap. Nothing here needs the layer
// stack either -- the chart is a dialog in the UA top layer, independent of it.
//
// The open dance is setLayerOpen's, not @starting-style, which Safari only
// recently learned: a dialog that showModal()s and gains .is-open in one
// synchronous block has no starting style, so the transition is skipped
// entirely. Un-hide, flush layout, then set the class.
const sizeChartBound = new WeakSet();

function bindSizeChart(dialog) {
  if (sizeChartBound.has(dialog)) return;
  sizeChartBound.add(dialog);
  // Esc would otherwise close instantly and skip the slide-out. `cancel` does
  // NOT bubble, so unlike the clicks this cannot be delegated at the document
  // and has to be bound per dialog -- hence the WeakSet, since a panel reload
  // brings a new element and a re-open must not stack a second listener.
  dialog.addEventListener('cancel', (event) => {
    event.preventDefault();
    closeSizeChart(dialog);
  });
  // Whichever route closed it, the class must not survive: showModal() on a
  // dialog still carrying .is-open starts at translateX(0) and never slides in.
  dialog.addEventListener('close', () => dialog.classList.remove('is-open'));
}

function openSizeChart(dialog) {
  // Feature detected rather than assumed, and guarded against being open
  // already: showModal throws InvalidStateError on an open dialog.
  if (typeof dialog.showModal !== 'function' || dialog.open) return;
  bindSizeChart(dialog);
  dialog.showModal();
  void dialog.offsetWidth; // flush layout so translateX(100%) is the start value
  dialog.classList.add('is-open');
}

// close() deferred to transitionend so the slide-out actually draws, with the
// same timeout deadline the layers use -- panelDeadline reads the element's own
// computed duration, so this cannot drift from the CSS, and under
// prefers-reduced-motion it collapses to the 80ms grace.
function closeSizeChart(dialog) {
  if (!dialog.open) return;
  if (!dialog.classList.contains('is-open')) {
    dialog.close();
    return;
  }
  dialog.classList.remove('is-open');
  const finish = () => {
    dialog.removeEventListener('transitionend', onEnd);
    clearTimeout(timer);
    if (!dialog.classList.contains('is-open') && dialog.open) dialog.close();
  };
  const onEnd = (event) => {
    if (event.target === dialog && event.propertyName === 'transform') finish();
  };
  const timer = setTimeout(finish, panelDeadline(dialog));
  dialog.addEventListener('transitionend', onEnd);
}

function onSizeChartClick(event) {
  const opener = event.target.closest('[data-open-size-chart]');
  if (opener) {
    const dialog = opener.closest('.size-chart').querySelector('[data-size-chart]');
    if (dialog) openSizeChart(dialog);
    return;
  }
  const closer = event.target.closest('[data-close-size-chart]');
  if (closer) {
    closeSizeChart(closer.closest('[data-size-chart]'));
    return;
  }
  // The backdrop. A <dialog> does not dismiss on a backdrop press by itself,
  // and the press lands on the dialog ELEMENT rather than on anything inside
  // it, which is what distinguishes it from a press on the panel. Only reaches
  // anyone on desktop, where the panel leaves a sliver of the page beside it.
  const panel = event.target.closest('[data-size-chart]');
  if (panel && event.target === panel) closeSizeChart(panel);
}

document.addEventListener('click', onSizeChartClick);

// --- Landing sticky size drop-up ------------------------------------------
// The drop-up owns NO state. It reports the radio group in the landing form and
// writes back to it, so the in-page size row and this control cannot disagree --
// there is one piece of state and both are views onto it. It carries no form
// field for the same reason: a second name="id" would be serialised by
// onAddToBag alongside the real radios and post two ids.
function sizeDropupParts() {
  const root = document.querySelector('[data-size-dropup]');
  if (!root) return null;
  const trigger = root.querySelector('[data-size-dropup-trigger]');
  const list = root.querySelector('[data-size-dropup-list]');
  const current = root.querySelector('[data-size-dropup-current]');
  if (!trigger || !list || !current) return null;
  return { root, trigger, list, current };
}

function landingSizeRadios() {
  return Array.from(document.querySelectorAll('#landing-form input[name="id"]'));
}

function setDropupOpen(parts, open) {
  parts.list.hidden = !open;
  parts.trigger.setAttribute('aria-expanded', String(open));
}

// Reads the group; never writes to it. Called on open, after a choice, and on
// any change to the row in the page, so the trigger always names the size that
// would actually be posted.
function syncDropupFromForm(parts) {
  const checked = landingSizeRadios().find((radio) => radio.checked);
  if (!checked) return;
  // The radio's own <span> is the size text; .textContent of the label would
  // pick up nothing else here, but the span is the thing that holds it.
  const label = checked.closest('.sizes__option');
  const text = label ? label.textContent.trim() : checked.value;
  if (text) parts.current.textContent = text;
  parts.list.querySelectorAll('[data-size-dropup-option]').forEach((option) => {
    if (option.dataset.variantId === checked.value) option.setAttribute('aria-current', 'true');
    else option.removeAttribute('aria-current');
  });
}

function onSizeDropupClick(event) {
  const parts = sizeDropupParts();
  if (!parts) return;

  if (event.target.closest('[data-size-dropup-trigger]')) {
    const isOpen = parts.trigger.getAttribute('aria-expanded') === 'true';
    if (!isOpen) syncDropupFromForm(parts);
    setDropupOpen(parts, !isOpen);
    return;
  }

  const option = event.target.closest('[data-size-dropup-option]');
  if (option) {
    // Matched by value rather than through a built selector: variant ids are
    // numeric today, and finding the node avoids ever putting one into a
    // selector string.
    const radio = landingSizeRadios().find((r) => r.value === option.dataset.variantId);
    if (radio && !radio.disabled) {
      radio.checked = true;
      // Bubbles, so anything watching the group hears this exactly as it would
      // hear a tap on the radio itself.
      radio.dispatchEvent(new Event('change', { bubbles: true }));
    }
    syncDropupFromForm(parts);
    setDropupOpen(parts, false);
    return;
  }

  // A press anywhere outside closes it. Includes Add to Cart beside it, which
  // is what you want: the bag opens over the page and a menu left standing
  // would be waiting underneath when it closes.
  if (!event.target.closest('[data-size-dropup]')) setDropupOpen(parts, false);
}

document.addEventListener('click', onSizeDropupClick);

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;
  const parts = sizeDropupParts();
  if (parts) setDropupOpen(parts, false);
});

// The row in the page is the other view onto the same radios. Delegated,
// because this listens for changes made anywhere in that form.
document.addEventListener('change', (event) => {
  if (!event.target.matches || !event.target.matches('#landing-form input[name="id"]')) return;
  const parts = sizeDropupParts();
  if (parts) syncDropupFromForm(parts);
});

// --- Landing sticky add to cart -------------------------------------------
// Shows once the real control has scrolled out of sight, so the two are never
// on screen together. An IntersectionObserver on that control rather than a
// scroll listener: no work on frames where nothing crossed, and the threshold
// is the element itself rather than a guessed pixel offset that a different
// product title or viewport would move.
//
// The bar is a plain submit button pointing at the product form through the
// HTML5 form attribute, so it needs no variant wiring of its own — it posts
// whatever size is selected above, through the same onAddToBag, and the cart
// slides over exactly as from the main button. With JS off it never appears,
// which is correct: it is an enhancement, not the way to buy.
function bindLandingCta() {
  if (!document.body.dataset.landing) return;
  const bar = document.querySelector('[data-landing-cta]');
  const anchor = document.querySelector('[data-landing-cta-anchor]');
  if (!bar || !anchor) return;

  if (!('IntersectionObserver' in window)) return;
  const observer = new IntersectionObserver(
    ([entry]) => {
      const visible = !entry.isIntersecting;
      bar.classList.toggle('is-visible', visible);
      // A drop-up left open rides down with the bar -- it is a child, so it
      // slides off-screen still marked open, and would be standing there on the
      // next scroll down. Closed on the way out instead.
      if (!visible) {
        const parts = sizeDropupParts();
        if (parts) setDropupOpen(parts, false);
      }
    },
    { threshold: 0 }
  );
  observer.observe(anchor);
}

// --- Layer stack ----------------------------------------------------------
if (hasLayerStack) {
  // setFlow restores each layer's scroll itself; the browser's own restore on
  // popstate would scroll the window to wherever the history entry says, fight
  // it, and land the homepage at the product panel's position.
  if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
  window.addEventListener('popstate', () => {
    pushedEntries = Math.max(0, pushedEntries - 1);
    // On the landing page the path says "product" and layer--product is EMPTY.
    // Closing the cart popped back to the landing URL, this resolved it as a
    // product panel, and applyStack slid that empty layer over the page and
    // dimmed the landing page behind it.
    //
    // Written out here rather than imported from theme-lib. NEVER ADD A NEW
    // NAMED EXPORT TO theme-lib.js AND IMPORT IT HERE: theme.js is loaded
    // through asset_url, which appends a ?v= that changes with the file, but the
    // relative specifier './theme-lib.js' resolves WITHOUT one. So theme.js
    // updates while theme-lib.js can still come from cache, the named import is
    // missing, and a missing named import fails the WHOLE module graph at link
    // time -- no handlers bind and every page reverts to a full navigation,
    // sitewide. theme-lib.js exports layerStackForPage with the same policy and
    // the tests cover it; this is deliberately a second copy of one expression.
    const stack = layerStackFor(location.pathname);
    applyStack(document.body.dataset.landing ? { product: false, cart: stack.cart } : stack);
  });

  document.addEventListener('click', onCardClick);
  document.addEventListener('click', onCartClick);
  document.addEventListener('click', onViewToggle);
  syncViewButtons();
  document.addEventListener('click', onCloseClick);
  document.addEventListener('click', onOutsideCartClick);
  document.addEventListener('click', onAddClick);
  bindCartSwipe();

  bindPanel(layers.home);
  hydrateDirectEntry();
  bindLandingCta();
}

document.documentElement.dataset.themeJs = 'ready';
